import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import {
  findUserAsStudent, getAllClasses,
  getAllLiveCourses, getModulesByCourse,
  retrieveCourse,
} from '@/api/school';

dayjs.extend(advancedFormat);

const initialState = () => ({
  courseId: null,
  courses: {},
  modules: {},
  classes: {},
  courseModules: {},
  moduleClasses: {},
  fetched: false,
  modulesFetched: {},
  classesFetched: {},
});

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    setCourseId(state, id) {
      state.courseId = id;
    },
    setCourses(state, payload) {
      state.courses = payload;
    },
    setFetched(state, value) {
      state.fetched = value;
    },
    setModules(state, payload) {
      state.modules = payload;
    },
    setClasses(state, payload) {
      state.classes = payload;
    },
    setCourseModules(state, { id, data }) {
      state.courseModules[id] = data;
    },
    setModuleClasses(state, payload) {
      state.moduleClasses = payload;
    },
    setModuleFetched(state, { id, value }) {
      state.modulesFetched[id] = value;
    },
    setClassFetched(state, { id, value }) {
      state.classesFetched[id] = value;
    },
    reset(state) {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async findStudent({ commit, dispatch, getters }) {
      try {
        if (getters.courseId) return;
        commit('setCourseId', await findUserAsStudent());
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
      }
    },
    async findCourse({ commit, dispatch, getters }) {
      try {
        commit('setCourse', await retrieveCourse(getters.courseId));
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
      }
    },
    async fetchCourses({ state, commit, dispatch }) {
      if (state.fetched) return;

      try {
        const { data: courses } = await getAllLiveCourses();
        commit('setCourses', Object.fromEntries(courses.map(course => [course.id, course])));
        commit('setFetched', true);
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
      }
    },
    async fetchModulesAndClasses({ state, commit, dispatch }) {
      await dispatch('fetchCourses');

      if (state.fetched) {
        if (state.modulesFetched[state.courseId]) return;
        try {
          const { data: modules } = await getModulesByCourse(state.courseId);
          commit('setModuleFetched', { id: state.courseId, value: true });
          const modulesMap = Object.fromEntries(modules.map(module => [module.id, module]));

          commit('setCourseModules', { id: state.courseId, data: modules });
          commit('setModules', { ...state.modules, ...modulesMap });

          if (state.classesFetched[state.courseId]) return;
          const { data: classes } = await getAllClasses(state.courseId);
          commit('setClassFetched', { id: state.courseId, value: true });
          const classesMap = Object.fromEntries(classes.map(classItem => [classItem.id, classItem]));

          const moduleClasses = {};
          classes.forEach((classItem) => {
            if (!moduleClasses[classItem.moduleId]) {
              moduleClasses[classItem.moduleId] = [];
            }
            moduleClasses[classItem.moduleId].push(classItem);
          });

          commit('setModuleClasses', { ...state.moduleClasses, ...moduleClasses });
          commit('setClasses', { ...state.classes, ...classesMap });
        } catch (e) {
          dispatch('errorOverlay/setMessage', e, { root: true });
        }
      }
    },
  },
  getters: {
    course: state => state.courses[state.courseId],
    courses: state => Object.values(state.courses),
    coursesFetched: state => state.fetched,
    courseId: state => state.courseId,
    moduleClasses: state => state.moduleClasses,
    modules: state => state.courseModules[state.courseId] || [],
    courseStartDate: (state, getters) => getters.course && getters.course.startDate,
    courseStartDateFormatted: (state, getters) => getters.courseStartDate
      ? dayjs(getters.courseStartDate).format('Do MMMM YYYY')
      : null,
    courseIsRunning: (state, getters) => getters.courseStartDate
      ? Date.now() > getters.courseStartDate
      : false,
  },
};
